import React from 'react';
import { motion } from 'framer-motion';
import { Flag, Sun, Ruler, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/Button';
const benefits = [{
  icon: Sun,
  title: 'Indoor & Outdoor',
  description: 'Durable materials built to withstand the elements or look sharp inside'
}, {
  icon: Ruler,
  title: 'Any Size You Need',
  description: 'From small table displays to large-format building banners'
}, {
  icon: Flag,
  title: 'Multiple Formats',
  description: 'Vinyl banners, mesh banners, fabric banners, retractable stands, and more'
}];
const faqs = [{
  question: 'What banner materials do you offer?',
  answer: 'We offer 13oz and 18oz vinyl for standard banners, mesh vinyl for windy outdoor locations, fabric banners for indoor displays, and specialty materials for retractable banner stands.'
}, {
  question: 'What sizes can you print?',
  answer: 'We can print banners from small tabletop sizes up to large format banners. Common sizes include 2x4, 3x6, 4x8, and custom dimensions. Contact us for oversized or specialty requirements.'
}, {
  question: 'Are your banners suitable for outdoor use?',
  answer: 'Yes. Our vinyl banners are UV-resistant and weatherproof. For high-wind areas, we recommend mesh banners that allow air to pass through while maintaining visibility.'
}, {
  question: 'What finishing options are available?',
  answer: "We offer hemmed edges, grommets (standard is every 2 feet), pole pockets, wind slits, and reinforced corners. Let us know your mounting needs and we'll recommend the best finish."
}, {
  question: 'How long do banners typically last?',
  answer: 'Indoor banners can last years with proper care. Outdoor vinyl banners typically last 2-5 years depending on weather exposure. We use UV-resistant inks to maximize longevity.'
}, {
  question: 'Can you help with banner design?',
  answer: 'Yes. We offer free design assistance to ensure your banner looks professional and is optimized for the size and viewing distance. Send us your logo and ideas to get started.'
}];
export function CustomBanners() {
  return <div className="min-h-screen bg-atomic-black" data-id="element-748">
      {/* Hero Section */}
      <section className="relative py-20 md:py-32 overflow-hidden" data-id="element-749">
        <div className="absolute inset-0 z-0" data-id="element-750">
          <img src="https://cdn.magicpatterns.com/uploads/1hR28F6b2srw261Ldm4BQP/custom-banners-michigan-commercial-signage.jpg" alt="Custom commercial banner displayed on building exterior" className="absolute inset-0 w-full h-full object-cover" data-id="element-751" />
          <div className="absolute inset-0 bg-atomic-black/80" data-id="element-752" />
          <div className="absolute inset-0 bg-gradient-to-b from-atomic-black/60 via-atomic-black/70 to-atomic-black/90" data-id="element-753" />
        </div>
        <div className="container mx-auto px-4 md:px-6 relative z-10" data-id="element-754">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="max-w-3xl mx-auto text-center" data-id="element-755">
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight" data-id="element-756">
              Custom Banners{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-atomic-green to-lime-400" data-id="element-757">
                That Get Noticed
              </span>
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed mb-8" data-id="element-758">
              High-quality vinyl, mesh, and fabric banners for events,
              storefronts, trade shows, and promotions. Any size, any quantity.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-759">
              <a href="/contact" data-id="element-760">
                <Button size="lg" data-id="element-761">
                  Request Banner Quote
                  <ArrowRight className="ml-2 w-4 h-4" data-id="element-762" />
                </Button>
              </a>
              <a href="tel:517-414-9232" data-id="element-763">
                <Button variant="outline" size="lg" data-id="element-764">
                  Call 517-414-9232
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-atomic-black border-t border-white/5" data-id="element-765">
        <div className="container mx-auto px-4 md:px-6" data-id="element-766">
          <div className="max-w-5xl mx-auto" data-id="element-767">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="text-center mb-12" data-id="element-768">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-769">
                Why Choose Our Custom Banners
              </h2>
              <p className="text-lg text-gray-300" data-id="element-770">
                Professional signage that makes an impact
              </p>
            </motion.div>
            <div className="grid md:grid-cols-3 gap-8" data-id="element-771">
              {benefits.map((benefit, index) => <motion.div key={benefit.title} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: index * 0.1
            }} className="bg-atomic-charcoal rounded-xl p-6 border border-white/10" data-id="element-772">
                  <div className="w-12 h-12 rounded-lg bg-atomic-green/10 flex items-center justify-center text-atomic-green mb-4 border border-atomic-green/20" data-id="element-773">
                    <benefit.icon size={24} data-id="element-774" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3" data-id="element-775">
                    {benefit.title}
                  </h3>
                  <p className="text-gray-400 leading-relaxed" data-id="element-776">
                    {benefit.description}
                  </p>
                </motion.div>)}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-atomic-charcoal border-t border-white/5" data-id="element-777">
        <div className="container mx-auto px-4 md:px-6" data-id="element-778">
          <div className="max-w-3xl mx-auto" data-id="element-779">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="text-center mb-12" data-id="element-780">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-781">
                Frequently Asked Questions
              </h2>
              <p className="text-lg text-gray-300" data-id="element-782">
                Common questions about custom banners
              </p>
            </motion.div>
            <div className="space-y-6" data-id="element-783">
              {faqs.map((faq, index) => <motion.div key={index} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: index * 0.05
            }} className="bg-atomic-black rounded-xl p-6 border border-white/10" data-id="element-784">
                  <h3 className="text-lg font-bold text-white mb-3" data-id="element-785">
                    {faq.question}
                  </h3>
                  <p className="text-gray-400 leading-relaxed" data-id="element-786">{faq.answer}</p>
                </motion.div>)}
            </div>
          </div>
        </div>
      </section>
    </div>;
}